import { memo, useCallback, useState } from "react";

import { BodySmall, BodyXS, Box, Button, Checkbox } from "@parspec/pixel";

import type { BulkSelectedRecord, ConflictPair } from "../types";
import RecordCheckboxCard from "./RecordCheckboxCard";
import { getTypeSafeDataProp } from "./utils";
import { CRM_ENTITY_TYPE } from "../constant";

interface ResolveConflictModalProps {
    handleNextStep: (selectedRecords: BulkSelectedRecord[]) => void;
    conflicts: Array<ConflictPair>,
}

const CONFLICT_RECORD = {
    DB_RECORD: "DB_RECORD",
    IMPORTED_RECORD: "IMPORTED_RECORD",
} as const;

type ConflictRecordValue = typeof CONFLICT_RECORD[keyof typeof CONFLICT_RECORD];

const headerTextSXStyle = { lineHeight: '1.25rem' };

const ResolveConflictsContent = memo(function ResolveConflictsContent(props: ResolveConflictModalProps) {
    const { handleNextStep, conflicts } = props;

    const [currentOpenedConflictIdx, setCurrentOpenedConflictIdx] = useState(0);
    const [isApplyToAll, setIsApplyToAll] = useState(false);

    // State to keep track of resolved conflict item of each pair
    const [resolvedConflicts, setResolvedConflicts] = useState<Array<ConflictRecordValue>>(
        new Array(conflicts.length).fill(CONFLICT_RECORD.IMPORTED_RECORD)
    );

    const currentOpenedConflict = conflicts[currentOpenedConflictIdx];
    const { dbRecord, importedRecord } = currentOpenedConflict;

    const onResolveConflict = () => {
        const isLastConflict = currentOpenedConflictIdx === conflicts.length - 1;

        if (isApplyToAll || isLastConflict) {
            const selectedRecords = conflicts.map((conflict, idx) => {
                const resolvedConflict = resolvedConflicts[idx];
                const isImported = resolvedConflict === CONFLICT_RECORD.IMPORTED_RECORD;
                return {
                    isImported,
                    type: conflict.type,
                    record: isImported ? conflict.importedRecord : conflict.dbRecord,
                } as BulkSelectedRecord;
            });

            handleNextStep(selectedRecords);
            return;
        }
        setCurrentOpenedConflictIdx(prev => prev + 1);
    };

    const onCardClick = useCallback((selectedCardId: ConflictRecordValue) => {
        // Handles the selection of a resolved card from a pair. 
        // Updates the value for the current conflict and all subsequent pairs.
        setResolvedConflicts((prev) => prev.map((val, idx) => idx >= currentOpenedConflictIdx
            ? selectedCardId : val
        ));
    }, [currentOpenedConflictIdx]);

    const cards = [{
        id: CONFLICT_RECORD.DB_RECORD,
        label: currentOpenedConflict.type === CRM_ENTITY_TYPE.COMPANY
            ? 'Keep existing Company record'
            : 'Keep existing Contact record',
        data: { record: dbRecord, type: currentOpenedConflict.type },
        labelSuffix: (
            <Box borderRadius={1} padding="4px 12px" bgcolor="tertiary.light">
                <BodyXS color="warning.main">Parspec</BodyXS>
            </Box>
        ),
    }, {
        id: CONFLICT_RECORD.IMPORTED_RECORD,
        label: currentOpenedConflict.type === CRM_ENTITY_TYPE.COMPANY
            ? 'Update Company record'
            : 'Update Contact record',
        data: { record: importedRecord, type: currentOpenedConflict.type },
        labelSuffix: (
            <Box borderRadius={1} padding="4px 12px" bgcolor="success.light">
                <BodyXS color="success.dark">Import</BodyXS>
            </Box>
        ),
    }];

    // The idx to show to user based on which pair user is resolving
    const currentReadableIdx = isApplyToAll ? conflicts.length : (currentOpenedConflictIdx + 1);
    return (
        <Box width={710} display="flex" flexDirection="column" gap={2}>
            <BodySmall limit={false} sx={headerTextSXStyle} color="text.secondary">
                These records have duplicated unique identifier(s). How would you like to proceed:
            </BodySmall>

            <Box display="flex" width="100%" gap={2}>
                {cards.map(({ id, data, ...rest }) => {
                    const typesafeData = getTypeSafeDataProp(data);

                    // It will never be null in real world scenario, but TS doesn't knows that
                    if (!typesafeData) return null;

                    return (
                        <Box key={id} flex={1} flexShrink={0}>
                            <RecordCheckboxCard
                                id={id}
                                checked={id === resolvedConflicts[currentOpenedConflictIdx]}
                                onCardClick={onCardClick}
                                data={typesafeData}
                                {...rest}
                            />
                        </Box>
                    )
                })}
            </Box>

            <Box display="flex" width="100%" justifyContent="flex-end" alignItems="center">
                <Checkbox
                    label={<BodySmall color="secondary.main" limit={false}>Apply to all</BodySmall>}
                    checked={isApplyToAll}
                    onChange={() => setIsApplyToAll(prev => !prev)}
                />
                <Button onClick={onResolveConflict}>
                    {`Resolve (${currentReadableIdx} of ${conflicts.length})`}
                </Button>
            </Box>
        </Box>
    )
});

export default ResolveConflictsContent;